import { z } from 'zod';
/**
 * Session 29 — bays platform contracts (migration 0016).
 *
 * `bays.schedules` is a JSONB array in the database; this file is its
 * write-boundary contract. v1 carries source text verbatim (`raw`,
 * `maxStay`) — the times grammar gets parsed into structured fields
 * (days, from/to, regime incl. event-day duality) when the map tab's
 * "open now" evaluation lands (S31). `.passthrough()` so readers built
 * against this version tolerate rows written by richer future versions.
 *
 * Literal enums mirror the 0016/0028 CHECK constraints — the database is the
 * enforcement; these exist for typed handling in importers and the API.
 */
export const baySourceSchema = z.enum(['user', 'council', 'tfl', 'osm', 'dtro']);
export const bayTypeSchema = z.enum([
    'loading_bay',
    'loading_place',
    'loading_dock',
    'parking',
    'restricted_bay',
    'no_loading',
    'no_waiting',
    'no_stopping',
]);
export const bayStatusSchema = z.enum(['unverified', 'confirmed', 'retired', 'disputed']);
export const bayScheduleSchema = z
    .object({
    /** Verbatim source text, e.g. "mon-sat 08:30-18:30" / "at any time". */
    raw: z.string().min(1),
    /** Verbatim max-stay text where the source provides one, e.g. "2 hours". */
    maxStay: z.string().min(1).optional(),
})
    .passthrough();
export const baySchedulesSchema = z.array(bayScheduleSchema);
/**
 * Session 31 — bays read API query contracts. Params arrive as strings;
 * z.coerce.number() parses them. Either a viewport `bbox` ("w,s,e,n") OR a
 * `lat`+`lng` centre (+ optional `radius` metres). Kept a plain object (not a
 * refine) so the fastify-zod type provider stays happy; baysService enforces
 * that exactly one mode is present.
 */
export const baysQuerySchema = z.object({
    lat: z.coerce.number().min(-90).max(90).optional(),
    lng: z.coerce.number().min(-180).max(180).optional(),
    radius: z.coerce.number().int().positive().max(5000).optional(),
    bbox: z.string().min(1).optional(),
    limit: z.coerce.number().int().positive().max(500).optional(),
    category: z.enum(['loading', 'parking', 'restricted']).optional(),
    /** S72: opt-in full footprint. Deliberately an enum, NOT z.coerce.boolean()
     *  — zod coerces ANY non-empty string (including "false") to true. Presence
     *  of '1'/'true' is the ask; omit for cheap centroid-only rows. */
    geometry: z.enum(['1', 'true']).optional(),
});
export const bayIdParamSchema = z.object({
    bayId: z.string().length(26),
});
/**
 * Session 32 — add-a-bay write contract. A driver at the kerb supplies only
 * what they can see: a point, a type, the sign's hours verbatim. The service
 * stamps source='user', status='unverified', geogApprox=true (a GPS-dropped
 * pin is never authoritative) and created_by — none are client-settable.
 * Importer-only provenance (usrn/traCode/streetName) is deliberately absent.
 */
export const bayMediaInputSchema = z.object({
    r2Key: z.string().trim().min(1).max(512),
    mimeType: z.string().trim().min(1).max(64),
    widthPx: z.number().int().positive().optional(),
    heightPx: z.number().int().positive().optional(),
    sizeBytes: z.number().int().positive(),
    altText: z.string().trim().max(2000).optional(),
});
export const bayCreateBodySchema = z.object({
    lng: z.number().min(-180).max(180),
    lat: z.number().min(-90).max(90),
    bayType: bayTypeSchema,
    description: z.string().trim().max(2000).optional(),
    /** Free-text sign hours, stored verbatim as schedules[0].raw. Empty = []. */
    hours: z.string().trim().min(1).max(500).optional(),
    media: z.array(bayMediaInputSchema).max(4).optional(),
});
/**
 * Session 34 — trust layer write contracts.
 *
 * Confirm (POST /v1/bays/:id/confirm) takes NO body — the act is the signal;
 * identity comes from auth, the bay from the path. >=2 distinct confirms flip
 * unverified -> confirmed (service-side). Hence no bayConfirmBodySchema.
 */
/** Flag a bay as wrong/gone. reason free-text, bounded like report reason. */
export const bayFlagBodySchema = z.object({
    reason: z.string().trim().min(2).max(80),
    details: z.string().trim().max(2000).optional(),
});
/**
 * Admin: resolve a pending flag. `status` mirrors reports (minus 'duplicate' —
 * a bay flag has no duplicate semantics). Optional `bayStatus` lets the admin
 * un-stick the bay in the same call (e.g. dismiss a bad-faith flag back to
 * 'confirmed', or action a genuine gone-bay to 'retired') — the escape hatch
 * for "two flags stranded a good bay". Omit to leave bay status untouched.
 */
export const bayFlagReviewBodySchema = z.object({
    status: z.enum(['dismissed', 'actioned']),
    reviewNotes: z.string().trim().max(2000).optional(),
    bayStatus: z.enum(['unverified', 'confirmed', 'retired']).optional(),
});
/** Path param for admin flag-review endpoints. */
export const bayFlagIdParamSchema = z.object({
    flagId: z.string().length(26),
});
//# sourceMappingURL=bay.js.map